// Per-user grid customization. Stored in Central as JSON keyed by
// (userId, resourceCode). Schema is versioned so we can evolve it.
//
// Defaults (no row in DB) come from the page's columnDefs.

export const CUSTOMIZATION_SCHEMA_VERSION = 1;

/** Aggregate function for the summary row (numeric columns only). */
export type ColumnSummary =
  | 'sum'
  | 'avg'
  | 'count'
  | 'min'
  | 'max'
  | null;

/** Excel-style format string from the closed list (see FORMAT_OPTIONS below). */
export type ColumnFormat = string | null;

export interface ColumnCustomization {
  /** Aggregate function to render in the pinned bottom row. Numeric columns only. */
  summary?: ColumnSummary;
  /** CSS color for the text. Null = inherit. */
  color?: string | null;
  /** CSS color for the background. Null = inherit. */
  backgroundColor?: string | null;
  /** Font family from the standard list. Null = inherit. */
  fontFamily?: string | null;
  bold?: boolean;
  italic?: boolean;
  /** Excel-style format string (free text, parsed at apply time; if the parser
   *  can't handle it, the raw value is shown — graceful degradation). */
  format?: ColumnFormat;
  /** Positive=green / negative=red / zero=column color or black. Numeric only. */
  signColoring?: boolean;
  /** Column width in px. Null/absent = AgGrid default for the column. */
  width?: number | null;
  /** Text alignment within the cell. Maps to CSS textAlign. */
  textAlign?: 'start' | 'center' | 'end' | null;
  /** Hide the column entirely. Maps to AgGrid colDef.hide. */
  hidden?: boolean;
}

export interface GridCustomization {
  /** Column field-names in display order. Missing fields fall back to colDef order. */
  columnOrder?: string[];
  /** Alternating row colors. */
  zebraStriping?: boolean;
  /** Auto-refresh interval in seconds. Null/0 = disabled. */
  autoRefreshSeconds?: number | null;
  /** Saved sort model, in sort-priority order (v0.1.84). Absent (old rows) =
   *  "don't touch the grid's sort"; present-but-empty = "explicitly unsorted". */
  sort?: Array<{ colId: string; sort: 'asc' | 'desc' }>;
}

export interface UserCustomizationSettings {
  version: typeof CUSTOMIZATION_SCHEMA_VERSION;
  grid: GridCustomization;
  /** Keyed by column field name (e.g. 'baseRate', 'assetId'). */
  columns: Record<string, ColumnCustomization>;
}

/** Empty/default customization used when there's no DB row. */
export function emptyCustomization(): UserCustomizationSettings {
  return {
    version: CUSTOMIZATION_SCHEMA_VERSION,
    grid: {},
    columns: {},
  };
}

// ---------- Format & Font catalogs (closed lists for v1) ----------

export interface FormatOption {
  /** The format string we store + send to the formatter. */
  value: string;
  /** Hebrew label shown in the dropdown. */
  label: string;
  /** Which column data type this applies to. */
  kind: 'number' | 'date';
}

export const FORMAT_OPTIONS: FormatOption[] = [
  { value: '0',              label: 'מספר שלם (0)',                    kind: 'number' },
  { value: '0.0',            label: 'ספרה אחת אחרי הנקודה (0.0)',     kind: 'number' },
  { value: '0.00',           label: 'שתי ספרות אחרי הנקודה (0.00)',  kind: 'number' },
  { value: '#,##0',          label: 'מספר עם פסיקי אלפים (1,234)',   kind: 'number' },
  { value: '#,##0.00',       label: 'אלפים + שתי ספרות (1,234.56)',  kind: 'number' },
  { value: '0%',             label: 'אחוז שלם (12%)',                 kind: 'number' },
  { value: '0.00%',          label: 'אחוז עם שתי ספרות (12.34%)',    kind: 'number' },
  { value: 'dd/MM/yyyy',     label: 'תאריך (28/05/2026)',             kind: 'date'   },
  { value: 'yyyy-MM-dd',     label: 'תאריך ISO (2026-05-28)',         kind: 'date'   },
  { value: 'dd/MM/yyyy HH:mm', label: 'תאריך + שעה (28/05/2026 14:30)', kind: 'date' },
  { value: 'MM/yy',         label: 'חודש (05/26)',                   kind: 'date'   },
  { value: 'HH:mm',          label: 'שעה (14:30)',                    kind: 'date'   },
];

/** Standard fonts — Hebrew-compatible web-safe set. 'inherit' = the page font. */
export const FONT_OPTIONS: { value: string; label: string }[] = [
  { value: 'inherit',          label: 'ברירת מחדל' },
  { value: 'Arial, sans-serif',          label: 'Arial' },
  { value: '"Times New Roman", serif',   label: 'Times New Roman' },
  { value: '"Courier New", monospace',   label: 'Courier New' },
  { value: 'Verdana, sans-serif',        label: 'Verdana' },
  { value: 'Georgia, serif',             label: 'Georgia' },
  { value: 'Tahoma, sans-serif',         label: 'Tahoma' },
];

export const SUMMARY_OPTIONS: { value: NonNullable<ColumnSummary>; label: string }[] = [
  { value: 'sum',   label: 'סכום' },
  { value: 'avg',   label: 'ממוצע' },
  { value: 'count', label: 'כמות' },
  { value: 'min',   label: 'מינימום' },
  { value: 'max',   label: 'מקסימום' },
];
